import { useEffect, useMemo, useState } from "react";

import { DOWNLOAD_LINKS, PLATFORM_IDS } from "../../data/siteContent";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  type CpuArch,
  detectCpuArch,
  detectCpuArchAsync,
  detectOs,
  resolveDownloadHref,
  resolvePlatformDownloadHref,
} from "../../lib/detectOs";
import { trackDownload } from "../../lib/trackDownload";
import { Section } from "../layout/Section";
import { Button } from "../ui/Button";
import { PlatformLogo } from "../ui/PlatformLogo";

import styles from "./Platforms.module.css";

function readNavigatorHints(): { userAgent: string; maxTouchPoints: number } {
  if (typeof navigator === "undefined") {
    return { userAgent: "", maxTouchPoints: 0 };
  }
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}

function macArchLabel(
  arch: "arm64" | "x64",
  labels: { arm64: string; x64: string; recommended: string },
  preferred: CpuArch,
): string {
  const base = arch === "arm64" ? labels.arm64 : labels.x64;
  if (preferred === arch) return `${base} · ${labels.recommended}`;
  return base;
}

export function Platforms() {
  const { copy } = useLocale();

  const hints = useMemo(() => readNavigatorHints(), []);
  const [cpuArch, setCpuArch] = useState<CpuArch>(() =>
    detectCpuArch(hints.userAgent, hints.maxTouchPoints),
  );

  useEffect(() => {
    let cancelled = false;
    void detectCpuArchAsync(hints).then((arch) => {
      if (!cancelled) setCpuArch(arch);
    });
    return () => {
      cancelled = true;
    };
  }, [hints]);

  const archHints = useMemo(
    () => ({
      userAgent: hints.userAgent,
      maxTouchPoints: hints.maxTouchPoints,
      uaArchitecture:
        cpuArch === "arm64" ? "arm" : cpuArch === "x64" ? "x86" : null,
    }),
    [hints, cpuArch],
  );

  const detectedOs = useMemo(
    () => detectOs(hints.userAgent),
    [hints.userAgent],
  );

  const osDownloadHref = useMemo(
    () => resolveDownloadHref(detectedOs, DOWNLOAD_LINKS, archHints),
    [detectedOs, archHints],
  );

  const trackOsDownload = () => {
    const platform =
      detectedOs === "windows" ||
      detectedOs === "macos" ||
      detectedOs === "linux"
        ? detectedOs
        : "windows";
    trackDownload(platform);
  };

  const platformDownloadHrefs = useMemo(
    () =>
      Object.fromEntries(
        PLATFORM_IDS.map((id) => [
          id,
          resolvePlatformDownloadHref(id, DOWNLOAD_LINKS, archHints),
        ]),
      ) as Record<(typeof PLATFORM_IDS)[number], string>,
    [archHints],
  );

  const macPreferredArch: CpuArch =
    detectedOs === "macos" ? cpuArch : "unknown";

  return (
    <Section id="platforms" title={copy.platforms.title}>
      <div className={styles.grid}>
        {PLATFORM_IDS.map((id) => {
          const platform = copy.platforms.cards[id];
          return (
            <article key={id} className={styles.card}>
              <PlatformLogo platform={id} />
              <h3 className={styles.cardTitle}>{platform.title}</h3>
              <p className={styles.cardText}>{platform.description}</p>

              {id === "macos" ? (
                <div className={styles.downloadStack}>
                  <Button
                    href={DOWNLOAD_LINKS.macos.arm64}
                    variant={
                      macPreferredArch === "arm64" ? "secondary" : "ghost"
                    }
                    className={styles.downloadBtn}
                    onClick={() => {
                      trackDownload("macos");
                    }}
                  >
                    {macArchLabel(
                      "arm64",
                      copy.platforms.macosArch,
                      macPreferredArch,
                    )}
                  </Button>
                  <Button
                    href={DOWNLOAD_LINKS.macos.x64}
                    variant={macPreferredArch === "x64" ? "secondary" : "ghost"}
                    className={styles.downloadBtn}
                    onClick={() => {
                      trackDownload("macos");
                    }}
                  >
                    {macArchLabel(
                      "x64",
                      copy.platforms.macosArch,
                      macPreferredArch,
                    )}
                  </Button>
                  <p className={styles.archHint}>
                    {copy.platforms.macosArch.hint}
                  </p>
                </div>
              ) : (
                <Button
                  href={platformDownloadHrefs[id]}
                  variant="ghost"
                  className={styles.downloadBtn}
                  onClick={() => {
                    trackDownload(id);
                  }}
                >
                  {copy.cta.download}
                </Button>
              )}
            </article>
          );
        })}
      </div>

      <p className={styles.note}>{copy.platforms.note}</p>
      <p className={styles.disclaimer}>
        {copy.platforms.distributionDisclaimer}
      </p>

      <div className={styles.mainCta}>
        <Button href={osDownloadHref} onClick={trackOsDownload}>
          {copy.cta.downloadForOs}
        </Button>
      </div>
    </Section>
  );
}
